import { useState } from "react";
import {
  DownloadSimple,
  PencilSimple,
  Plus,
  Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  title: "",
  due_date: "",
  amount: "",
  status: "outstanding",
  display_order: "0",
};

export function EventInvoicesTab({ eventId, invoices, onSaved }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setFile(null);
    setOpen(true);
  };
  const openEdit = (invoice) => {
    setEditing(invoice);
    setForm({
      ...invoice,
      amount: String(invoice.amount),
      display_order: String(invoice.display_order),
    });
    setFile(null);
    setOpen(true);
  };
  const download = async (invoice) => {
    try {
      const response = await api.get(
        `/staff/events/${eventId}/invoices/${invoice.id}/file`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = invoice.original_filename || invoice.title;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const save = async () => {
    if (!form.title.trim() || (!editing && !file)) {
      toast.error("Invoice title and document are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/staff/events/${eventId}/invoices/${editing.id}`, {
          ...form,
          amount: Number(form.amount),
          display_order: Number(form.display_order),
        });
      } else {
        const payload = new FormData();
        payload.append("file", file);
        Object.entries(form).forEach(([key, value]) =>
          payload.append(key, value),
        );
        await api.post(`/staff/events/${eventId}/invoices`, payload);
      }
      toast.success(
        editing ? "Invoice updated." : "Invoice shared with clients.",
      );
      setOpen(false);
      onSaved();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Event invoices</p>
          <p className="text-sm text-muted-foreground mt-1">
            Upload documents and manage display-only invoice status. No payment
            collection occurs here.
          </p>
        </div>
        <Button
          className="rounded-full bg-primary hover:bg-primary/90"
          onClick={openCreate}
          data-testid="add-invoice-btn"
        >
          <Plus size={17} className="mr-1" /> Add invoice
        </Button>
      </div>
      {invoices.length === 0 ? (
        <div className="mt-6 border border-dashed border-border p-8 text-center rounded-lg">
          <Receipt size={28} className="mx-auto text-secondary" />
          <p className="font-heading text-2xl text-primary mt-3">
            No invoices yet
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Upload an event invoice when it is ready to share.
          </p>
        </div>
      ) : (
        <div className="mt-6 divide-y divide-border border-y border-border">
          {invoices.map((invoice) => (
            <article
              key={invoice.id}
              className="flex flex-wrap items-center justify-between gap-4 py-5"
              data-testid={`staff-invoice-${invoice.id}`}
            >
              <div>
                <p className="font-heading text-2xl text-primary">
                  {invoice.title}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Due {formatDate(invoice.due_date)} ·{" "}
                  {currency(invoice.amount)} · order {invoice.display_order}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="rounded-full capitalize">
                  {invoice.status}
                </Badge>
                <IconButton
                  label={`Download ${invoice.title}`}
                  testId={`download-staff-invoice-${invoice.id}`}
                  onClick={() => download(invoice)}
                >
                  <DownloadSimple size={17} />
                </IconButton>
                <IconButton
                  label={`Edit ${invoice.title}`}
                  testId={`edit-staff-invoice-${invoice.id}`}
                  onClick={() => openEdit(invoice)}
                >
                  <PencilSimple size={17} />
                </IconButton>
              </div>
            </article>
          ))}
        </div>
      )}
      <InvoiceDialog
        open={open}
        editing={editing}
        form={form}
        file={file}
        saving={saving}
        onClose={() => setOpen(false)}
        onFile={setFile}
        onChange={(key, value) => setForm({ ...form, [key]: value })}
        onSave={save}
      />
    </div>
  );
}

function InvoiceDialog({
  open,
  editing,
  form,
  file,
  saving,
  onClose,
  onFile,
  onChange,
  onSave,
}) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            {editing ? "Edit invoice" : "Add invoice"}
          </DialogTitle>
          <DialogDescription>
            Invoice status is display-only; payment is managed outside the
            portal.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
          <InputField
            label="Invoice title"
            value={form.title}
            onChange={(value) => onChange("title", value)}
          />
          <InputField
            label="Due date"
            type="date"
            value={form.due_date}
            onChange={(value) => onChange("due_date", value)}
          />
          <InputField
            label="Amount (AUD)"
            type="number"
            value={form.amount}
            onChange={(value) => onChange("amount", value)}
          />
          <InputField
            label="Display order"
            type="number"
            value={form.display_order}
            onChange={(value) => onChange("display_order", value)}
          />
          <div>
            <Label className="eyebrow">Status</Label>
            <Select
              value={form.status}
              onValueChange={(value) => onChange("status", value)}
            >
              <SelectTrigger
                className="mt-2"
                data-testid="invoice-status-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="outstanding">Outstanding</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!editing && (
            <div>
              <Label className="eyebrow">Invoice document</Label>
              <Input
                type="file"
                accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg,.webp"
                className="mt-2"
                onChange={(event) => onFile(event.target.files?.[0] || null)}
                data-testid="invoice-file-input"
              />
              {file && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {file.name}
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="rounded-full bg-primary hover:bg-primary/90"
            onClick={onSave}
            disabled={saving}
            data-testid="save-invoice-btn"
          >
            {saving ? "Saving…" : editing ? "Save invoice" : "Upload invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InputField({ label, type = "text", value, onChange }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2"
        data-testid={`invoice-${label.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-")}-input`}
      />
    </div>
  );
}
function IconButton({ label, testId, onClick, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      data-testid={testId}
      className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-primary"
    >
      {children}
    </button>
  );
}
function currency(value) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(Number(value || 0));
}
function formatDate(value) {
  return value
    ? new Date(`${value}T12:00:00`).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "date to be confirmed";
}
